import { from, noop, Observable } from 'rxjs';
import { concatMap, map, take, tap, toArray } from 'rxjs/operators';
import { ActivatedRouteSnapshot } from '@angular/router';

export const isNull = (val: any): boolean => val === null;

export const isTrue = (val: any): boolean => !!val;

export const isFalse = (val: any): boolean => !val;

export const isString = (val: any): boolean => typeof val === 'string';

export const isEqualTo =
  (...args): any =>
  (valToCompare: any): boolean =>
    Array.from(args).some(d => d === valToCompare);

export const isUndefined = (val: any): boolean => val === undefined;

export const isValue = (val: any): boolean => !isUndefined(val) && !isNull(val);

export const isArray = (val: any): boolean => Array.isArray(val);

export const isObject = (val: any): boolean => val && !isNull(val) && !isArray(val) && typeof val === 'object';

export const isFunction = (val: any): boolean => typeof val === 'function';

export const asString = (val: any): string => (isString(val) ? val : isValue(val) ? `${val}` : '');

export const asNumber = (val: any, scale: number = null): number => {
  let num: number;

  if (!isValue(val)) {
    num = 0;
  } else {
    const toNum = +`${val}`.replace(/,/g, '');
    num = isNaN(toNum) ? 0 : toNum;
  }

  if (isNull(scale) || isNaN(scale)) {
    return num;
  } else {
    const pow = Math.pow(10, scale);
    return Math.round(num * pow) / pow;
  }
};

export const asFunction = (...args): any => {
  try {
    const argsArr = Array.from(args);
    const main = argsArr.splice(0, 1)[0];

    return isFunction(main) ? main.apply(null, argsArr) : main;
  } catch (e) {
    return console.error(e);
  }
};

export const asArray = <T = any>(d: any): T[] => (Array.isArray(d) ? d : [d]);

export const allIsTrue = (d: any) => asArray(d).every(isTrue);

export const isInRange = (valA: any, valB: any, diff: any = 1): boolean => {
  const nValA = asNumber(valA);
  const nValB = asNumber(valB);
  const nDiff = asNumber(diff);

  return nValA >= nValB - nDiff && nValA <= nValB + nDiff;
};

export function diffArray(firstData: any[] = [], secondData: any[] = []): any[] {
  const mergedData = secondData.concat(firstData);
  const uniqueIds = [];
  return mergedData.filter(element => {
    const isDuplicate = uniqueIds.includes(element.id);

    if (!isDuplicate) {
      uniqueIds.push(element.id);

      return true;
    }

    return false;
  });
}

export const digOut = (...args: any): any => {
  const argsArr = asArray(Array.from(args));
  const base = argsArr.splice(0, 1)[0];

  let i = 0;

  try {
    const lastIndex = argsArr.length - 1;
    let val = (base && base[argsArr[i]]) || undefined;

    if (val) {
      while (lastIndex > i++) {
        val = val[argsArr[i]];
        if (val === undefined) {
          i = lastIndex + 1;
        }
      }
    }

    return val;
  } catch (e) {
    return;
  }
};

export const digIn = <T = any>(target: T, path: string | any, patchQuery: any): T => {
  const steps = path.split('.');
  const len = steps.length;
  let level = target;

  try {
    for (let i = 0; i < len; i++) {
      const step = steps[i];

      if (i === len - 1) {
        level[step] = patchQuery;
      } else {
        level = level[step];
      }
    }

    return target;
  } catch (e) {
    return target;
  }
};

export const concatProps = <T = any>(source: T, propsList: any): string[] => {
  try {
    return asArray(propsList)
      .map(props =>
        asArray(props)
          .reduce((str, prop) => `${str} ${digOut(source, prop) || ''}`, '')
          .trim()
      )
      .filter(isTrue);
  } catch (e) {
    console.log(e);
    return [];
  }
};

export const checkParentClass = (elem: any, className: string): boolean => {
  let hasClass: boolean = false;
  let parentElem = elem;

  while (parentElem) {
    hasClass = !!parentElem.classList && parentElem.classList.contains(className);
    parentElem = hasClass ? null : parentElem.parentNode;
  }

  return hasClass;
};

export const getParentByClassName = (elem: any, className: string) => {
  let searchedElem: any = null;
  let parentElem = elem;

  while (parentElem) {
    searchedElem = searchedElem || (parentElem?.classList?.contains(className) ? parentElem : null);
    parentElem = !!searchedElem ? null : parentElem.parentNode;
  }

  return searchedElem;
};

export const isClickedInside = (root: any, target: any): boolean => {
  let clickedInside = false;

  while (target) {
    if (target === root) {
      target = null;
      clickedInside = true;
    } else {
      target = target.parentNode || null;
    }
  }

  return clickedInside;
};

export const handleClickOutside = (target: Element, rootElement: Element, action: any = noop): any =>
  !isClickedInside(rootElement, target) && action();

export const compareObjects = (objA: any, objB: any): boolean => JSON.stringify(objA) === JSON.stringify(objB);

export const compareValues = (valA: any, valB: any, checkIsNil: boolean = false): boolean => {
  const normalizeValue = (val): any => (isArray(val) ? (asArray(val)?.length ? val : null) : val);

  valA = normalizeValue(valA);
  valB = normalizeValue(valB);

  return !valA && !checkIsNil ? !valA === !valB : compareObjects({ val: valA }, { val: valB });
};

export const getObjectDifference = <T = any>(objA: any, objB: any, log: boolean = false): Partial<T> => {
  const changes: Partial<T> = {};

  try {
    Object.keys(objB).forEach(key => {
      const valA = objA[key];
      const valB = objB[key];
      const patch = { [key]: valB };

      log && console.log(valB, valA, key);

      (!!valA !== !!valB || !compareObjects({ [key]: valA }, patch)) && Object.assign(changes, patch);
    });

    log && Object.keys(changes).length && console.log(changes);

    return Object.keys(changes).length ? changes : null;
  } catch (err) {
    console.log(err);
    return null;
  }
};

export const cloneObject = <T = any>(d: T): T => JSON.parse(JSON.stringify(d));

export const toMap = <I, T>(d: any, indexBy: any = 'label'): Map<I, T> =>
  new Map<I, T>(asArray(d).map(dd => [dd[indexBy], dd]));

export const asPercent = (value: any, roundTo: number = 3): number => {
  const val = +value || 0;

  if (val <= 0 || isNaN(val)) {
    return 0;
  } else if (val >= 100) {
    return 100;
  } else {
    return +val.toFixed(roundTo);
  }
};

export const destruct = <T = any>(source: T, fields: any): Partial<T> => {
  return asArray(fields).reduce((obj, field) => Object.assign(obj, { [field]: digOut(source, field) }), {});
};

export const blurFromAny = (): void => {
  (document.activeElement as HTMLInputElement)?.blur();
};

export const exportDataToFile = (content: any, fileName: string, type: string = 'text/csv;charset=utf-8'): void => {
  const blob = new Blob([content], { type });
  const objUrl = URL.createObjectURL(blob);
  let link = document.createElement('a');

  link.setAttribute('href', objUrl);
  link.setAttribute('download', fileName);
  link.click();

  link = null;
};

export const pascalCaseToCamelCase = (val: string | any): string => {
  const subs = `${val}`.split('_');
  return subs
    .map((sub: string, i: number) => {
      return i ? [sub[0].toUpperCase(), sub.slice(1)].join('') : sub;
    })
    .join('');
};

export const observeMultiple = <T = any>(
  observables: Observable<any> | Observable<any>[],
  size: number = 50
): Observable<T> => {
  const results = [];
  const slices = [];
  const data = asArray(observables);

  while (data.length) {
    slices.push(data.splice(0, size));
  }

  return from(slices).pipe(
    concatMap(slice => {
      return from(slice).pipe(
        concatMap((d: Observable<any>) => d),
        toArray(),
        tap(res => results.push(...res))
      );
    }),
    toArray(),
    map(() => results),
    take(1)
  ) as Observable<unknown> as Observable<T>;
};

export const getPath = <T = any>(snapshot: ActivatedRouteSnapshot): T => {
  return digOut(snapshot, 'url', '0', 'path');
};
